import { useEffect, useState, useCallback } from 'react';
import Script from 'next/script';
import { hotjar } from 'react-hotjar';

const ThirdPartyScripts = () => {
  const [hasInteracted, setHasInteracted] = useState(false);
  const [shouldLoadNonCritical, setShouldLoadNonCritical] = useState(false);
  const [loadedScripts, setLoadedScripts] = useState<Set<string>>(new Set());

  // Define the interaction handler at the top level
  const handleInteraction = useCallback(() => {
    if (!hasInteracted) {
      setHasInteracted(true);
      // Use requestIdleCallback to defer non-critical work
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => {
          setShouldLoadNonCritical(true);
        });
      } else {
        // Fallback for browsers that don't support requestIdleCallback
        setTimeout(() => {
          setShouldLoadNonCritical(true);
        }, 0);
      }
    }
  }, [hasInteracted]);

  // Set up interaction listeners
  useEffect(() => {
    const events = ['click', 'scroll', 'touchstart', 'mousemove'];
    
    events.forEach(event => {
      window.addEventListener(event, handleInteraction, { passive: true });
    });

    // Set a timeout to load non-critical scripts after 5 seconds regardless of interaction
    const timeoutId = setTimeout(() => {
      if (!hasInteracted) {
        setShouldLoadNonCritical(true);
      }
    }, 5000);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleInteraction);
      });
      clearTimeout(timeoutId);
    };
  }, [handleInteraction, hasInteracted]);

  const shouldLoadScript = useCallback((isCritical: boolean) => {
    return isCritical || hasInteracted || shouldLoadNonCritical;
  }, [hasInteracted, shouldLoadNonCritical]);

  const handleScriptLoad = useCallback((scriptId: string) => {
    setLoadedScripts(prev => new Set(prev).add(scriptId));
  }, []);

  return (
    <>
      {/* Critical GTM initialization - Load immediately */}
      <Script
        id="gtm-init"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${process.env.NEXT_PUBLIC_GTM_ID}', {
              page_path: window.location.pathname,
            });
          `,
        }}
      />

      {/* GTM Base - Load after interactive */}
      {shouldLoadScript(true) && !loadedScripts.has('gtm-base') && (
        <Script
          id="gtm-base"
          strategy="afterInteractive"
          onLoad={() => handleScriptLoad('gtm-base')}
          dangerouslySetInnerHTML={{
            __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer', '${process.env.NEXT_PUBLIC_GTM_ID}');
            `,
          }}
        />
      )}

      {/* GTM Analytics - Load after interactive */}
      {shouldLoadScript(true) && !loadedScripts.has('gtm-analytics') && (
        <Script
          id="gtm-analytics"
          strategy="afterInteractive"
          onLoad={() => handleScriptLoad('gtm-analytics')}
          src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GTM_ID}`}
        />
      )}

      {/* HubSpot - Load only after interaction or delay */}
      {shouldLoadScript(false) && !loadedScripts.has('hubspot') && (
        <Script
          id="hubspot"
          strategy="lazyOnload"
          onLoad={() => {
            handleScriptLoad('hubspot');
            // Initialize HubSpot chat only when needed
            if (window.hbspt) {
              // Use requestIdleCallback for non-critical initialization
              if ('requestIdleCallback' in window) {
                window.requestIdleCallback(() => {
                  window.hbspt.forms.create({
                    portalId: process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID,
                    formId: process.env.NEXT_PUBLIC_HUBSPOT_FORM_ID,
                    target: '#hubspot-form-container'
                  });
                });
              } else {
                setTimeout(() => {
                  window.hbspt.forms.create({
                    portalId: process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID,
                    formId: process.env.NEXT_PUBLIC_HUBSPOT_FORM_ID,
                    target: '#hubspot-form-container'
                  });
                }, 0);
              }
            }
          }}
          src={`//js.hs-scripts.com/${process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID}.js`}
        />
      )}

      {/* Hotjar - Load only after interaction or delay */}
      {shouldLoadScript(false) && !loadedScripts.has('hotjar') && (
        <Script
          id="hotjar"
          strategy="lazyOnload"
          onLoad={() => {
            handleScriptLoad('hotjar');
            // Initialize Hotjar in the next idle period
            if ('requestIdleCallback' in window) {
              window.requestIdleCallback(() => {
                const hotJARID = parseInt(process.env.NEXT_PUBLIC_HOTJAR_KEY_ID as any, 10);
                const hotJARCode = parseInt(process.env.NEXT_PUBLIC_HOTJAR_CODE as any, 10);
                hotjar.initialize(hotJARID as number, hotJARCode as number);
              });
            } else {
              setTimeout(() => {
                const hotJARID = parseInt(process.env.NEXT_PUBLIC_HOTJAR_KEY_ID as any, 10);
                const hotJARCode = parseInt(process.env.NEXT_PUBLIC_HOTJAR_CODE as any, 10);
                hotjar.initialize(hotJARID as number, hotJARCode as number);
              }, 0);
            }
          }}
          dangerouslySetInnerHTML={{
            __html: `
              (function(h,o,t,j,a,r){
                h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                h._hjSettings={hjid:${process.env.NEXT_PUBLIC_HOTJAR_KEY_ID},hjsv:6};
                a=o.getElementsByTagName('head')[0];
                r=o.createElement('script');r.async=1;
                r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                a.appendChild(r);
              })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
            `,
          }}
        />
      )}

      {/* Stripe - Load only after interaction or delay */}
      {shouldLoadScript(false) && !loadedScripts.has('stripe') && (
        <Script
          id="stripe"
          strategy="lazyOnload"
          onLoad={() => handleScriptLoad('stripe')}
          src="https://js.stripe.com/v3/"
        />
      )}

      {/* Google Maps - Load only after interaction or delay */}
      {shouldLoadScript(false) && !loadedScripts.has('google-maps') && (
        <Script
          id="google-maps"
          strategy="lazyOnload"
          onLoad={() => handleScriptLoad('google-maps')}
          src={`https://maps.googleapis.com/maps/api/js?key=${process.env.GOOGLE_PLEACES_KEY}&libraries=places`}
        />
      )}

      {/* Reb2b - Load immediately as critical tracking script */}
      {shouldLoadScript(true) && !loadedScripts.has('reb2b') && (
        <Script
          id="reb2b"
          strategy="afterInteractive"
          onLoad={() => handleScriptLoad('reb2b')}
          dangerouslySetInnerHTML={{
            __html: `
              !function () {
                var reb2b = window.reb2b = window.reb2b || []; 
                if (reb2b.invoked) return;
                reb2b.invoked = true;
                reb2b.methods = ["identify", "collect"]; 
                reb2b.factory = function (method) {
                  return function () {
                    var args = Array.prototype.slice.call(arguments); 
                    args.unshift(method);
                    reb2b.push(args);
                    return reb2b;
                  };
                }; 
                for (var i = 0; i < reb2b.methods.length; i++) {
                  var key = reb2b.methods[i];
                  reb2b[key] = reb2b.factory(key);
                } 
                reb2b.load = function (key) {
                  var script = document.createElement("script");
                  script.type = "text/javascript";
                  script.async = true; 
                  script.src = "https://s3-us-west-2.amazonaws.com/b2bjsstore/b/" + key + "/QO92DH7Y5RN7.js.gz"; 
                  var first = document.getElementsByTagName("script")[0]; 
                  first.parentNode.insertBefore(script, first);
                }; 
                reb2b.SNIPPET_VERSION = "1.0.1";
                reb2b.load("QO92DH7Y5RN7");
              }();
            `,
          }}
        />
      )}
    </>
  );
};

export default ThirdPartyScripts; 